import React, { Fragment, isValidElement } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { HeadingText60, BodyTextSmall, Button } from '@modernhealth/vital-components';

import { Spinner } from 'src/shared/components/CenteredSpinner';

export { ProviderSelectionCard } from './ProviderSelectionCard';
export { ProviderSelectionContactUsBanner } from './ProviderSelectionContactUsBanner';
export { ProviderSelectionError } from './ProviderSelectionError';
export { ProviderSelectionPreferencesModal } from './ProviderSelectionPreferencesModal';
export type { ProviderSelectionPreferences } from './ProviderSelectionPreferencesModal';

export interface ProviderSelectionProps<T extends { uuid: string }> {
    title: JSX.Element | string;
    subtitle: JSX.Element | string;
    providers: T[];
    renderProvider: (provider: T, index: number) => JSX.Element;
    loading?: boolean;
    error?: JSX.Element | null;
    onBackClick?: () => void;
    onPreferencesClick?: () => void;
    activePreferences?: number;
}

export function ProviderSelection<T extends { uuid: string }>({
    title,
    subtitle,
    providers,
    renderProvider,
    loading = false,
    error,
    onBackClick,
    onPreferencesClick,
    activePreferences = 0,
}: ProviderSelectionProps<T>) {
    const { t } = useTranslation();

    return (
        <Container>
            {!!onBackClick && (
                <Button
                    aria-label={t('common.back')}
                    icon="arrow-left"
                    variant="flat"
                    css={{ position: 'absolute', top: 0, left: 0 }}
                    onClick={onBackClick}
                />
            )}
            <HeadingText60 fontColor="gray85" marginBottom={10}>
                {title}
            </HeadingText60>
            <BodyTextSmall fontColor="gray65" as="h2">
                {subtitle}
            </BodyTextSmall>
            {isValidElement(error) && error}
            {!!onPreferencesClick && (
                <Button
                    onClick={onPreferencesClick}
                    variant={activePreferences > 0 ? 'primary' : 'secondary'}
                    icon="care-team"
                    size="small"
                    rounded
                >
                    {activePreferences > 0
                        ? t('providers:preferences_button.text_active', {
                              count: activePreferences,
                          })
                        : t('providers:preferences_button.text')}
                </Button>
            )}
            {loading && <Spinner size={60} css={{ marginTop: 100 }} />}
            {!loading && providers.length > 0 && (
                <ProvidersListWrapper>
                    {providers.map((provider, i) => (
                        <Fragment key={provider.uuid}>{renderProvider(provider, i)}</Fragment>
                    ))}
                </ProvidersListWrapper>
            )}
        </Container>
    );
}

const Container = styled.div({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 40,
});

const ProvidersListWrapper = styled.ul({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: 0,
    marginTop: 32,
});
